<template>
  <div class="events-toolbar-actions">
    <div class="actions-left">
      <button
        v-if="displayFiltersBtn"
        :class="[{ active: filtersExpanded }, 'filter-button']"
        :style="styleOverrides"
        :aria-label="$t('events.filters')"
        :aria-expanded="String(filtersExpanded)"
        :aria-controls="filtersExpanded ? 'event-mobile-filters' : undefined"
        @click="$emit('collapse-menu')"
      >
        <FilterIcon />
      </button>
    </div>
    <div
      :style="styleOverrides"
      class="actions-right"
    >
      <div
        v-if="currentView !== 'EventsSearchView'"
        class="actions-right-buttons"
      >
        <YearDropdown />
        <div
          v-if="!isMobile"
          class="list-views-container"
        >
          <button
            v-for="[key, meta] in listViews"
            :id="meta.componentName"
            ref="viewTypes"
            :key="key"
            :style="styleOverrides"
            :class="[{ active: currentView === meta.componentName }, 'view-button']"
            :aria-pressed="String(currentView === meta.componentName)"
            :data-cy="`change-view-${meta.componentName}`"
            @click="$emit('change-view', key, $event.currentTarget)"
          >
            <component :is="meta.iconComponent" />
            <p class="button-text">{{ meta.displayName }}</p>
          </button>
        </div>
        <div
          v-if="!isMobile && currentView === 'EventsListView'"
          :style="styleOverrides"
          class="list-toggle-container"
        >
          <div class="list-toggle">
            <button
              :class="{ selected: listViewType === 'all' }"
              :aria-pressed="String(listViewType === 'all')"
              @click="selectListViewType('all')"
            >
              {{ $t('events.filterUpcomingEvents') }}
            </button>
            <button
              :class="{ selected: listViewType === 'month' }"
              :aria-pressed="String(listViewType === 'month')"
              @click="selectListViewType('month')"
            >
              {{ $t('events.filterByMonth') }}
            </button>
          </div>
        </div>
      </div>
      <button
        v-else
        class="close-icon"
        @click="resetSearch"
      >
        <CloseIcon />
        <span class="sr-only">{{ $t('events.clearSearch') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { navigateTo, useNuxtApp } from '#imports';
import FilterIcon from './icons/FilterIcon.vue';
import ListViewIcon from './icons/ListViewIcon.vue';
import CalendarViewIcon from './icons/CalendarViewIcon.vue';
import CloseIcon from './icons/CloseIcon.vue';
import YearDropdown from './EventViews/shared/YearDropdown.vue';
import { EVENT_VIEW_LIST } from './helpers/events.helper.js';
import { getMonthRange } from '../../../helpers/events-state.helper.js';
import { useEventsStore } from '../../../stores/eventsStore.js';
import { useMainStore } from '../../../stores/mainStore.js';

defineOptions({ name: 'EventsToolbarActions' });

const props = defineProps({
  isMobile: Boolean,
  filtersExpanded: Boolean,
});

defineEmits(['collapse-menu', 'change-view']);

const { $intl } = useNuxtApp();
const eventsStore = useEventsStore();
const mainStore = useMainStore();
const route = useRoute();

const iconComponents = { ListViewIcon, CalendarViewIcon };

const filters = computed(() => eventsStore.filters ?? []);
const hasSections = computed(() => eventsStore.hasSections ?? false);
const queryParams = computed(() => eventsStore.queryParams ?? {});
const currentView = computed(() => eventsStore.currentView);
const listViewType = computed(() => eventsStore.listViewType);
const headingColor = computed(() => mainStore.getThemeHeadingColor ?? '#333');

const listViews = computed(() =>
  Object.entries(EVENT_VIEW_LIST($intl)).map(([key, meta]) => [
    key,
    { ...meta, iconComponent: iconComponents[meta.iconName] },
  ]),
);

const styleOverrides = computed(() => {
  const color = headingColor.value;
  return {
    '--button-primary-bg': `${color}1A`,
    '--button-primary-bg-hover': `${color}0D`,
    '--button-primary-text': '#333333',
    '--button-border-radius': '8px',
    '--button-border-active': `1px solid ${color}`,
    '--button-outline': 'none',
  };
});

const displayFiltersBtn = computed(() => props.isMobile && (hasSections.value || filters.value.length));

function resetSearch() {
  eventsStore.updateSearchText('');
  return navigateTo({
    query: {
      id: undefined,
      search: undefined,
    },
  });
}

async function selectListViewType(view) {
  const viewType = view === 'all' ? 'list-all' : 'list-month';
  const date = new Date(eventsStore.currentDate);
  const monthRange = getMonthRange(date.getFullYear(), date.getMonth());
  const params = {
    ...queryParams.value,
    view: viewType,
    start_date: view === 'all' ? undefined : monthRange.start_date,
    end_date: view === 'all' ? undefined : monthRange.end_date,
  };
  eventsStore.setListViewType(view);
  eventsStore.setQueryParams(params);
  return navigateTo({
    query: {
      ...route.query,
      ...params,
    },
  });
}
</script>

<style lang="scss" scoped>
@use '../../global/scss/mixins/flex_mixin';
@use '../../global/scss/mixins/breakpoints';

.events-toolbar-actions {
  @include flex_mixin.flex-row;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  .actions-left,
  .actions-right {
    .a-button {
      &:focus {
        outline: none;
        border: 2px solid blue;
      }
    }
    @include flex_mixin.flex-row;
    margin-right: 8px;
    &:last-child {
      margin-right: 0;
    }
  }
  .actions-right {
    .actions-right-buttons {
      @include flex_mixin.flex-row;
      flex-wrap: wrap;
      justify-content: flex-end;
    }
  }

  @media (min-width: breakpoints.$lg-min-width) {
    .actions-left {
      display: none;
    }
  }
  @include breakpoints.screen('<=sm') {
    .actions-left {
      .filter-button {
        background-color: var(--button-primary-bg);
        border-radius: var(--button-border-radius);
        border: none;
        height: max-content;
      }
      .active {
        border: var(--button-border-active);
        background-color: var(--button-primary-bg-hover);
      }
    }
    .actions-right {
      .actions-right-buttons {
        .view-header {
          margin-right: 10px;
        }
        .view-button {
          font-family: inherit;
          margin: 0 4px;
          background-color: var(--button-primary-bg);
          border-radius: var(--button-border-radius);
          border: none;
          height: max-content;
        }
        .active {
          border: var(--button-border-active);
          background-color: var(--button-primary-bg-hover);
        }
      }
    }
  }
  @include breakpoints.screen('>=md') {
    .actions-right {
      .actions-right-buttons {
        .list-views-container {
          display: flex;
        }
        .view-button {
          font-family: inherit;
          margin: 0 4px;
          background-color: var(--button-primary-bg);
          border-radius: var(--button-border-radius);
          border: none;
          height: max-content;
          display: flex;
          align-items: center;
          padding: 8px 13px;
        }
        .active {
          border: var(--button-border-active);
          background-color: var(--button-primary-bg-hover);
        }
      }
    }
  }
}

.button-text {
  @media (max-width: breakpoints.$lg-min-width) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
}

.close-icon {
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  height: 33px;

  svg {
    padding: 10px;
    background-color: var(--button-primary-bg-hover);
  }
  &:hover {
    cursor: pointer;
  }
}

.list-toggle-container {
  z-index: 0;
  pointer-events: none;
  flex-basis: 100%;
  margin-top: 24px;
  .list-toggle {
    pointer-events: auto;
    display: flex;
    align-items: center;
    float: right;
    margin-right: 4px;
    border-radius: 4px;
    border: 1px solid var(--button-primary-bg-hover);
    button {
      border: none;
      padding: 0 8px;
      margin: 8px;
      border-radius: 4px;
      background: inherit;
      font-size: 12px;
      min-height: 24px;
      min-width: 24px;
      &:hover {
        cursor: pointer;
      }
      &.selected {
        background: var(--button-primary-bg-hover);
      }
    }
  }
}
</style>
